import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  getConfigurationAsync,
  getCarrierList,
  getPlantList,
} from '@/service/AuthenticationService';
import { initializeMiddlewareClient, isMiddlewareClientInitialized } from '@/service/taskService';
import {
  useAppDispatch,
  setBootstrapConfig,
  setBootstrapError,
  setCarriers,
  setPlants,
} from '@/store';
import { PlantInfo } from '@/types';

import type { ConfigurationResponse } from '@/service/types/authenticatetypes';
import type { CarrierDTO } from '@/service/types/usertypes';

// ─────────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────────

export const authQueryKeys = {
  all: ['auth'] as const,
  config: ['auth', 'config'] as const,
  carriers: ['auth', 'carriers'] as const,
  plants: ['auth', 'plants'] as const,
};

// ─────────────────────────────────────────────────────────────────
// Query Hooks
// ─────────────────────────────────────────────────────────────────

export const useBootstrapConfigQuery = (enabled = true) => {
  const dispatch = useAppDispatch();
  const hasSynced = useRef(false);

  const query = useQuery({
    queryKey: authQueryKeys.config,
    queryFn: async (): Promise<ConfigurationResponse> => {
      return getConfigurationAsync();
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  console.log('queryyy', query.data);

  // Sync server state → Redux client state
  useEffect(() => {
    if (query.data && !hasSynced.current) {
      hasSynced.current = true;
      dispatch(setBootstrapConfig(query.data));

      // Initialize middleware client with MIDDLEWARE_API_BASE_URL from config settings.
      // In dev mode, use Vite proxy (/middleware/) to avoid CORS issues.

      // eslint-disable-next-line no-console
      console.log('mode', import.meta.env.DEV);
      const middlewareBaseUrl = import.meta.env.DEV
        ? '/middleware/'
        : query.data.settings.MIDDLEWARE_API_BASE_URL;
      if (middlewareBaseUrl && !isMiddlewareClientInitialized()) {
        initializeMiddlewareClient(middlewareBaseUrl, {});
      }
    }
  }, [query.data, dispatch]);

  useEffect(() => {
    if (query.error) {
      const message =
        query.error instanceof Error ? query.error.message : 'Failed to load configuration';
      dispatch(setBootstrapError(message));
    }
  }, [query.error, dispatch]);

  return query;
};

export const useCarriersQuery = (enabled = true) => {
  const dispatch = useAppDispatch();
  const hasSynced = useRef(false);

  const query = useQuery({
    queryKey: authQueryKeys.carriers,
    queryFn: async (): Promise<CarrierDTO[]> => {
      const response = await getCarrierList();
      return response.carriers;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });

  // Sync server state → Redux client state
  useEffect(() => {
    if (query.data && !hasSynced.current) {
      hasSynced.current = true;
      dispatch(setCarriers(query.data));
    }
  }, [query.data, dispatch]);

  return query;
};

export const usePlantsQuery = (enabled = true) => {
  const dispatch = useAppDispatch();
  const hasSynced = useRef(false);

  const query = useQuery({
    queryKey: authQueryKeys.plants,
    queryFn: async (): Promise<PlantInfo[]> => {
      const response = await getPlantList();
      return response;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
  // Sync server state → Redux client state
  useEffect(() => {
    if (query.data && !hasSynced.current) {
      hasSynced.current = true;
      dispatch(setPlants(query.data));
    }
  }, [query, dispatch]);

  return query;
};
